import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';

const WeatherForecast = () => {
  const [forecastData, setForecastData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Puerto Princesa Subterranean River National Park coordinates
  const LATITUDE = 10.1744;
  const LONGITUDE = 118.9186;

  const fetchWeatherData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Open-Meteo API - free weather API, no key required
      const response = await axios?.get('https://api.open-meteo.com/v1/forecast', {
        params: {
          latitude: LATITUDE,
          longitude: LONGITUDE,
          daily: 'temperature_2m_max,temperature_2m_min,precipitation_probability_max,weathercode,windspeed_10m_max,uv_index_max',
          timezone: 'Asia/Manila',
          forecast_days: 5
        }
      });

      const { daily } = response?.data;
      
      const formattedData = daily?.time?.map((date, index) => {
        const weatherCode = daily?.weathercode?.[index];
        const condition = getWeatherCondition(weatherCode);
        
        return {
          date: format(new Date(date), 'MMM d'),
          day: index === 0 ? 'Today' : index === 1 ? 'Tomorrow' : format(new Date(date), 'EEEE'),
          temp: {
            high: Math.round(daily?.temperature_2m_max?.[index]),
            low: Math.round(daily?.temperature_2m_min?.[index])
          },
          condition: condition?.description,
          icon: condition?.icon,
          precipitation: Math.round(daily?.precipitation_probability_max?.[index]),
          humidity: 70 + Math.random() * 20, // Estimated based on tropical climate
          wind: Math.round(daily?.windspeed_10m_max?.[index]),
          uv: Math.round(daily?.uv_index_max?.[index] || 0),
          weatherCode: weatherCode
        };
      });

      setForecastData(formattedData);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching weather data:', err);
      setError('Failed to load weather data. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeatherData();
    // Refresh weather data every 30 minutes
    const interval = setInterval(fetchWeatherData, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getWeatherCondition = (code) => {
    // WMO Weather interpretation codes
    const conditions = {
      0: { description: 'Clear Sky', icon: 'Sun' },
      1: { description: 'Mainly Clear', icon: 'Sun' },
      2: { description: 'Partly Cloudy', icon: 'CloudSun' },
      3: { description: 'Overcast', icon: 'Cloud' },
      45: { description: 'Foggy', icon: 'Cloud' },
      48: { description: 'Foggy', icon: 'Cloud' },
      51: { description: 'Light Drizzle', icon: 'CloudDrizzle' },
      53: { description: 'Drizzle', icon: 'CloudDrizzle' },
      55: { description: 'Heavy Drizzle', icon: 'CloudDrizzle' },
      61: { description: 'Light Rain', icon: 'CloudRain' },
      63: { description: 'Moderate Rain', icon: 'CloudRain' },
      65: { description: 'Heavy Rain', icon: 'CloudRain' },
      80: { description: 'Rain Showers', icon: 'CloudRain' },
      81: { description: 'Rain Showers', icon: 'CloudRain' },
      82: { description: 'Heavy Rain Showers', icon: 'CloudRain' },
      95: { description: 'Thunderstorms', icon: 'CloudLightning' },
      96: { description: 'Thunderstorms', icon: 'CloudLightning' },
      99: { description: 'Severe Thunderstorms', icon: 'CloudLightning' }
    };
    
    return conditions?.[code] || { description: 'Unknown', icon: 'Cloud' };
  };

  const getConditionColor = (condition) => {
    if (condition?.includes("Clear") || condition?.includes("Sunny")) return "text-[var(--color-accent)]";
    if (condition?.includes("Rain") || condition?.includes("Thunderstorms")) return "text-[var(--color-info)]";
    return "text-[var(--color-muted-foreground)]";
  };

  const getAlertLevel = (precipitation) => {
    if (precipitation >= 70) return { color: "var(--color-error)", text: "High Risk" };
    if (precipitation >= 40) return { color: "var(--color-warning)", text: "Moderate Risk" };
    return { color: "var(--color-success)", text: "Good for Visit" };
  };

  const getBestDayRecommendation = () => {
    if (forecastData?.length === 0) return null;
    
    // Find the best day based on: low precipitation, good UV, moderate temperature
    const scoredDays = forecastData?.map((day, index) => {
      let score = 0;
      
      // Lower precipitation is better
      score += (100 - day?.precipitation) * 0.4;
      
      // Temperature between 24-29°C is ideal
      const avgTemp = (day?.temp?.high + day?.temp?.low) / 2;
      if (avgTemp >= 24 && avgTemp <= 29) score += 30;
      else if (avgTemp >= 22 && avgTemp <= 31) score += 15;
      
      // Lower wind is better
      score += (30 - Math.min(day?.wind, 30)) * 0.5;
      
      // Clear skies are better
      if (day?.condition?.includes("Clear") || day?.condition?.includes("Sunny")) score += 20;
      else if (day?.condition?.includes("Partly")) score += 10;
      
      return { ...day, score, index };
    });
    
    const bestDay = scoredDays?.reduce((best, current) => 
      current?.score > best?.score ? current : best
    );
    
    return bestDay;
  };

  const bestDay = getBestDayRecommendation();

  if (loading) {
    return (
      <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)]">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary)]"></div>
          <span className="ml-3 text-[var(--color-muted-foreground)]">Loading weather data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)]">
        <div className="flex items-center justify-center py-12">
          <Icon name="AlertCircle" size={24} color="var(--color-error)" />
          <span className="ml-3 text-[var(--color-error)]">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)]">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-[var(--color-info)]">
            <Icon name="CloudSun" size={24} color="var(--color-info-foreground)" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-[var(--color-foreground)]">5-Day Weather Forecast</h3>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Real-time weather • Last updated: {lastUpdated ? format(lastUpdated, 'h:mm a') : 'N/A'}
            </p>
          </div>
        </div>
        <button
          onClick={fetchWeatherData}
          className="p-2 rounded-lg hover:bg-[var(--color-accent)] hover:bg-opacity-10 transition-colors"
          title="Refresh weather data"
        >
          <Icon name="RefreshCw" size={20} color="var(--color-muted-foreground)" />
        </button>
      </div>
      {bestDay && (
        <div className="mb-6 p-4 bg-[var(--color-success)] bg-opacity-10 border border-[var(--color-success)] rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Calendar" size={20} color="var(--color-success)" className="mt-0.5" />
            <div>
              <p className="font-medium text-[var(--color-success)] mb-1">Best Day for Your Visit</p>
              <p className="text-sm text-[var(--color-foreground)]">
                <span className="font-semibold">{bestDay?.day}</span> ({bestDay?.date}) offers the best conditions with {bestDay?.condition?.toLowerCase()}, 
                {bestDay?.temp?.high}°C high temperature, and only {bestDay?.precipitation}% chance of rain.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {forecastData?.map((day, index) => {
          const alert = getAlertLevel(day?.precipitation);
          const isBestDay = bestDay && bestDay?.index === index;
          
          return (
            <div 
              key={index}
              className={`bg-[var(--color-background)] rounded-lg p-4 border transition-all ${
                isBestDay 
                  ? 'border-[var(--color-success)] ring-2 ring-[var(--color-success)] ring-opacity-30' 
                  : 'border-[var(--color-border)] hover:border-[var(--color-primary)]'
              }`}
            >
              {isBestDay && (
                <div className="mb-2 text-center">
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-success)] bg-[var(--color-success)] bg-opacity-10 px-2 py-1 rounded-full">
                    <Icon name="Star" size={12} color="var(--color-success)" />
                    Best Day
                  </span>
                </div>
              )}
              <div className="text-center mb-3">
                <p className="text-sm font-medium text-[var(--color-foreground)]">{day?.day}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">{day?.date}</p>
              </div>
              <div className="flex justify-center mb-3">
                <Icon name={day?.icon} size={40} className={getConditionColor(day?.condition)} />
              </div>
              <div className="text-center mb-3">
                <p className="text-2xl font-bold text-[var(--color-foreground)]">{day?.temp?.high}°</p>
                <p className="text-sm text-[var(--color-muted-foreground)]">{day?.temp?.low}°</p>
              </div>
              <p className="text-xs text-center text-[var(--color-muted-foreground)] mb-3">
                {day?.condition}
              </p>
              <div className="space-y-2 pt-3 border-t border-[var(--color-border)]">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1">
                    <Icon name="Droplets" size={12} color="var(--color-info)" />
                    <span className="text-[var(--color-muted-foreground)]">Rain</span>
                  </div>
                  <span className="font-medium text-[var(--color-foreground)]">{day?.precipitation}%</span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1">
                    <Icon name="Wind" size={12} color="var(--color-muted-foreground)" />
                    <span className="text-[var(--color-muted-foreground)]">Wind</span>
                  </div>
                  <span className="font-medium text-[var(--color-foreground)]">{day?.wind} km/h</span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1">
                    <Icon name="Sun" size={12} color="var(--color-accent)" />
                    <span className="text-[var(--color-muted-foreground)]">UV</span>
                  </div>
                  <span className="font-medium text-[var(--color-foreground)]">{day?.uv}</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                <div className="flex items-center justify-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: alert?.color }} />
                  <span className="text-xs font-medium" style={{ color: alert?.color }}>
                    {alert?.text}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 p-4 bg-[var(--color-info)] bg-opacity-10 border border-[var(--color-info)] rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-info)" className="mt-0.5" />
          <div>
            <p className="font-medium text-[var(--color-info)] mb-1">Weather Advisory</p>
            <p className="text-sm text-[var(--color-foreground)]">
              Weather data updates every 30 minutes. Click the refresh icon to get the latest forecast. 
              Plan your trip on days marked as "Good for Visit" for the best experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherForecast;